import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPin, Save, Building2 } from "lucide-react";
import { toast } from "sonner";

const COVERAGE = {
  lumis: { label: "LUMIS verified sources", cls: "bg-emerald-100 text-emerald-800" },
  org: { label: "Organization-uploaded sources", cls: "bg-[#FDECD0] text-[#7C3F00]" },
  none: { label: "No sources currently available", cls: "bg-slate-100 text-slate-500" },
};

export function CoverageBadge({ coverage, testid }) {
  const c = COVERAGE[coverage] || COVERAGE.none;
  return <span data-testid={testid} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${c.cls}`}>{c.label}</span>;
}

const FEDERAL_LABELS = {
  private: "Federal law for private/nonprofit employers",
  state_local: "Federal law for state/local government employers",
  federal: "Federal-sector law and regulation for federal agencies (Title 5)",
  unspecified: "Federal law (employer type not set)",
};
export const NO_STATE_NOTICE = "State and local sources are not included until a primary work state is selected.";

export function labelFor(codes, data, context = "unspecified") {
  const names = {};
  data?.states?.forEach((s) => { names[s.code] = s.name; s.locals.forEach((l) => { names[l.code] = l.name; }); });
  const mid = (codes || []).filter((c) => c !== "US" && c !== "ORG").map((c) => `${names[c] || c} sources`);
  return [FEDERAL_LABELS[context] || FEDERAL_LABELS.unspecified, ...mid, "organizational policy library"].join(" + ");
}

export default function JurisdictionSelector({ value, onChange, compact }) {
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/jurisdictions").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (value == null && data) onChange(data.user_default.length ? data.user_default : data.org_default);
  }, [data, value, onChange]);

  const active = value || [];
  const toggle = (code, isLocal, parent) => {
    setDraft((d) => {
      if (d.includes(code)) return d.filter((c) => c !== code && !(isLocal ? false : c.startsWith(code + "-")));
      return isLocal ? [...new Set([...d, parent, code])] : [...d, code];
    });
  };
  const apply = () => { onChange(draft); setOpen(false); };
  const saveDefault = async () => {
    try { const { data: u } = await api.patch("/auth/preferences", { jurisdictions: draft }); setUser(u); toast.success("Saved as your default work jurisdiction"); onChange(draft); setOpen(false); }
    catch { toast.error("Could not save default"); }
  };
  const saveOrg = async () => {
    try { await api.put("/settings/jurisdictions", { codes: draft }); toast.success("Organization default updated"); setData((d) => ({ ...d, org_default: draft })); }
    catch { toast.error("Could not save organization default"); }
  };
  const states = (data?.states || []).filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()) || s.code.toLowerCase() === q.toLowerCase());

  return (
    <>
      <div data-testid="jurisdiction-context" className={`flex flex-wrap items-center gap-2 ${compact ? "text-xs" : "text-sm"}`}>
        <MapPin className="w-4 h-4 text-[#C7761A]" />
        <span className="text-slate-600">Jurisdictional context:</span>
        <span data-testid="jurisdiction-label" className="font-semibold text-[#0A2A5E]">{labelFor(active, data, user?.employer_context || "unspecified")}</span>
        <button data-testid="change-jurisdiction-btn" onClick={() => { setDraft(active); setOpen(true); }} className="text-[#7C3F00] font-semibold underline underline-offset-2 hover:text-[#0A2A5E]">Change jurisdiction</button>
        {!(active || []).some((c) => c !== "US" && c !== "ORG") && <span data-testid="no-state-notice" className="basis-full text-[11px] text-slate-500">{NO_STATE_NOTICE}</span>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display">Select jurisdictions</DialogTitle>
            <DialogDescription>Federal and your organization's policy library are always included. Select the employee's primary work state (not headquarters); locals appear under their state. Select more than one for remote or multistate workers.</DialogDescription>
          </DialogHeader>
          <Input data-testid="jurisdiction-search" placeholder="Search states…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" />
          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
            {states.map((s) => (
              <div key={s.code} className="px-3 py-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid={`jur-state-${s.code}`} checked={draft.includes(s.code)} onCheckedChange={() => toggle(s.code, false)} />
                  <span className="font-medium text-slate-800 flex-1">{s.name}</span>
                  <CoverageBadge coverage={s.coverage} testid={`jur-coverage-${s.code}`} />
                </label>
                {draft.includes(s.code) && s.locals.length > 0 && (
                  <div className="ml-7 mt-1 space-y-1">
                    {s.locals.map((l) => (
                      <label key={l.code} className="flex items-center gap-2 text-xs cursor-pointer">
                        <Checkbox data-testid={`jur-local-${l.code}`} checked={draft.includes(l.code)} onCheckedChange={() => toggle(l.code, true, s.code)} />
                        <span className="flex-1 text-slate-700">{l.name}</span>
                        <CoverageBadge coverage={l.coverage} testid={`jur-coverage-${l.code}`} />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p data-testid="jurisdiction-disclaimer" className="text-[11px] text-slate-500 leading-relaxed">{data?.disclaimer}</p>
          <div className="flex flex-wrap gap-2 justify-end">
            {user?.role === "manager" && <Button data-testid="jurisdiction-save-org" variant="outline" size="sm" onClick={saveOrg} className="border-slate-300"><Building2 className="w-4 h-4 mr-1.5" /> Set organization default</Button>}
            <Button data-testid="jurisdiction-save-default" variant="outline" size="sm" onClick={saveDefault} className="border-[#F5A623] text-[#7C3F00]"><Save className="w-4 h-4 mr-1.5" /> Save as my default</Button>
            <Button data-testid="jurisdiction-apply" size="sm" onClick={apply} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">Use for this question</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
